import type { RevenueAnalyticsColumnVisibility } from 'src/admin/LAB/RevenueAnalytics/columns';
import { DEFAULT_REVENUE_ANALYTICS_COLUMN_VISIBILITY } from 'src/admin/LAB/RevenueAnalytics/columns';

/**
 * Fixed page sizes offered by the Prev/Next pager. There is no "all"
 * option: the table pages the backend with `skip`/`limit` rather than
 * loading the full result set client-side.
 */
export type RevenueAnalyticsRowsPerPage = 25 | 50 | 100;

export const REVENUE_ANALYTICS_PAGE_SIZE_OPTIONS: readonly RevenueAnalyticsRowsPerPage[] = [25, 50, 100];

export const DEFAULT_REVENUE_ANALYTICS_ROWS_PER_PAGE: RevenueAnalyticsRowsPerPage = 50;

/**
 * Server-sortable columns; each maps to a field of the backend's OData
 * row shape. The remaining columns (fair value and the inventory
 * figures) are not part of that shape, so they are not sortable.
 */
export type RevenueAnalyticsSortColumn = 'provider' | 'symbol' | 'qty' | 'avgPrice' | 'totalCost' | 'curPrice';

export type RevenueAnalyticsSortDirection = 'asc' | 'desc';

export interface RevenueAnalyticsSort {
    readonly column: RevenueAnalyticsSortColumn;
    readonly direction: RevenueAnalyticsSortDirection;
}

/**
 * Columns with a header filter; each maps to a numeric field of the
 * backend's OData row shape. Provider and symbol are filtered by their
 * dedicated controls, and the remaining columns are not in that shape.
 */
export type RevenueAnalyticsFilterColumn = 'qty' | 'avgPrice' | 'totalCost' | 'curPrice';

export const REVENUE_ANALYTICS_FILTER_COLUMNS: readonly RevenueAnalyticsFilterColumn[] = [
    'qty',
    'avgPrice',
    'totalCost',
    'curPrice',
];

/** Committed filter text per column; `''` means no filter on that column. */
export type RevenueAnalyticsColumnFilters = Readonly<Record<RevenueAnalyticsFilterColumn, string>>;

export const EMPTY_REVENUE_ANALYTICS_COLUMN_FILTERS: RevenueAnalyticsColumnFilters = {
    qty: '',
    avgPrice: '',
    totalCost: '',
    curPrice: '',
};

export interface RevenueAnalyticsState {
    /**
     * Firm scope for the provider list. `undefined` is the superuser "all
     * firms" view once `setFirm` has run; before that it is merely the
     * pre-initialization value of a fresh instance (the queries stay
     * disabled until the scope is set).
     */
    readonly firmId: string | undefined;
    /** Selected provider ids; empty shows the all-provider aggregate. */
    readonly selectedProviderIds: ReadonlyArray<string>;
    /** Live text in the symbol search box. */
    readonly symbolSearchInput: string;
    /** Committed symbol term sent to the backend; `''` when search is inactive. */
    readonly appliedSymbolSearch: string;
    /** 1-based page index used to derive `$skip`. */
    readonly page: number;
    readonly rowsPerPage: RevenueAnalyticsRowsPerPage;
    /** Column sort sent as OData `$orderby`; `null` uses the backend default. */
    readonly sort: RevenueAnalyticsSort | null;
    /** Committed per-column filter text, compiled into the OData `$filter`. */
    readonly columnFilters: RevenueAnalyticsColumnFilters;
    /** Which columns the table shows; persisted per browser by the model. */
    readonly columnVisibility: RevenueAnalyticsColumnVisibility;
    /** True while a CSV export is fetching and building the file. */
    readonly isDownloadingCsv: boolean;
    /** True when the last CSV export failed; cleared when a new export starts. */
    readonly csvDownloadFailed: boolean;
}

export const initialRevenueAnalyticsState: RevenueAnalyticsState = {
    firmId: undefined,
    selectedProviderIds: [],
    symbolSearchInput: '',
    appliedSymbolSearch: '',
    page: 1,
    rowsPerPage: DEFAULT_REVENUE_ANALYTICS_ROWS_PER_PAGE,
    sort: null,
    columnFilters: EMPTY_REVENUE_ANALYTICS_COLUMN_FILTERS,
    columnVisibility: DEFAULT_REVENUE_ANALYTICS_COLUMN_VISIBILITY,
    isDownloadingCsv: false,
    csvDownloadFailed: false,
};
